import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Button, Avatar, Container, Divider, IconButton, TextField, Select, MenuItem, FormControl, InputLabel, Alert } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import MessageIcon from '@mui/icons-material/Message';
import PersonIcon from '@mui/icons-material/Person';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import WarningIcon from '@mui/icons-material/Warning';
import { keyframes } from '@mui/system';
import { useAuth } from '../contexts/AuthContext';
import { getMessagesForUser, sendMessage } from '../services/messagesService';
import apiClient from '../services/apiClient';
import socketClient from '../services/socketClient';

// Animation keyframes
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Messages = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [userType, setUserType] = useState('patient');
  const [messages, setMessages] = useState([]);
  const [peopleList, setPeopleList] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [selectedRecipient, setSelectedRecipient] = useState('');
  const [pilotMode, setPilotMode] = useState(false);

  useEffect(() => {
    const type = searchParams.get('userType') || 'patient';
    setUserType(type);

    const fetchUsers = async () => {
      try {
        const roleToFetch = type === 'physician' ? 'patient' : 'physician';
        const response = await apiClient.getUsers({ role: roleToFetch });
        const users = response.rows || response.data || response || [];
        setPeopleList(users.map(u => ({ 
          id: u.id, 
          name: `${u.first_name || u.firstName || ''} ${u.last_name || u.lastName || ''}`.trim() 
        })));
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };

    // Check pilot mode status
    const checkPilotMode = async () => {
      try {
        const response = await apiClient.get('/health/pilot-mode');
        if (response.data) {
          setPilotMode(response.data.pilotMode || false);
        }
      } catch (error) {
        console.warn('Failed to check pilot mode:', error);
      }
    };

    fetchUsers();
    checkPilotMode();
  }, [searchParams]);

  useEffect(() => {
    if (user) {
      // Fetch initial messages
      getMessagesForUser(user.id).then(({ data }) => {
        if (data) {
          setMessages(data);
        }
      });

      // Connect to socket for real-time updates
      socketClient.connect();

      // Listen for new messages
      const handleNewMessage = (messageData) => {
        if (messageData.senderId === user.id || messageData.receiverId === user.id) {
          setMessages(prev => {
            // Avoid duplicates
            if (prev.find(m => m.id === messageData.id)) return prev;
            return [...prev, messageData];
          });
        }
      };

      socketClient.on('message:new', handleNewMessage);

      // Cleanup on unmount
      return () => {
        socketClient.off('message:new', handleNewMessage);
      };
    }
  }, [user]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedRecipient) return;

    const messageData = {
      senderId: user.id,
      receiverId: selectedRecipient,
      content: newMessage,
      tenantId: user.tenant_id,
    };

    const { data, error } = await sendMessage(messageData);
    if (error) {
      console.error('Error sending message:', error);
    } else {
      // Message will be added via socket event, but add optimistically
      const newMsg = Array.isArray(data) ? data[0] : data;
      setMessages(prev => [...prev, newMsg]);
      setNewMessage('');
      
      // Also send via socket for real-time delivery
      socketClient.sendMessage({
        recipientId: selectedRecipient,
        content: newMessage,
        senderId: user.id,
      });
    }
  };

  const pageTitle = userType === 'physician' ? 'Patient Messages' : 'Messages';
  const emptyMessage = userType === 'physician' ? 'No patient messages yet.' : 'No messages from your care team yet.';

  return (
    <Container maxWidth="lg" sx={{ py: 4, px: { xs: 2, sm: 3, md: 4 } }}>
      <Paper 
        elevation={0} 
        sx={{ 
          p: { xs: 2, sm: 3, md: 4 }, 
          borderRadius: 4, 
          maxWidth: 800, 
          width: '100%', 
          mx: 'auto',
          position: 'relative',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
          animation: `${fadeIn} 0.5s ease-out`,
          overflow: 'hidden',
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          mb: 3,
          flexDirection: 'column'
        }}>
          <Avatar 
            sx={{ 
              bgcolor: 'info.main', 
              width: 56, 
              height: 56, 
              mb: 2,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            }}
          >
            <MessageIcon fontSize="large" />
          </Avatar>
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: 700,
              color: 'text.primary',
              textAlign: 'center'
            }}
          >
            {pageTitle}
          </Typography>
        </Box>
        
        <Divider sx={{ mb: 3 }} />

        {/* Pilot Mode Warning */}
        {pilotMode && (
          <Alert 
            severity="warning" 
            icon={<WarningIcon />}
            sx={{ mb: 3 }}
          >
            <strong>Pilot Mode:</strong> Please do not enter Protected Health Information (PHI) in messages. 
            Use only test data or non-identifiable information during this pilot phase.
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="recipient-select-label">Recipient</InputLabel>
            <Select
              labelId="recipient-select-label"
              value={selectedRecipient}
              label="Recipient"
              onChange={(e) => setSelectedRecipient(e.target.value)}
            >
              {peopleList.map((person) => (
                <MenuItem key={person.id} value={person.id}>{person.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            variant="outlined"
            label="New Message"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            multiline
            rows={3}
          />
          <Button
            variant="contained"
            color="primary"
            endIcon={<SendIcon />}
            onClick={handleSendMessage}
            sx={{ mt: 2 }}
          >
            Send
          </Button>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Box sx={{ mb: 3 }}>
          {messages.length === 0 ? (
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 2,
                bgcolor: 'background.paper',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                textAlign: 'center',
                color: 'text.secondary'
              }}
            >
              <Typography>
                {emptyMessage}
              </Typography>
            </Paper>
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              {messages.map((msg, index) => (
                <Box
                  key={msg.id}
                  sx={{
                    display: 'flex',
                    p: 2,
                    borderRadius: 2,
                    bgcolor: msg.sender_id === user.id ? 'primary.light' : 'background.paper',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                    transition: 'all 0.3s ease',
                    animation: `${fadeIn} 0.5s ease-out ${index * 0.1}s both`,
                  }}
                >
                  <Avatar sx={{ bgcolor: 'info.light', mr: 2, alignSelf: 'flex-start' }}>
                    <PersonIcon />
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {msg.sender_id === user.id ? 'You' : peopleList.find(p => p.id === msg.sender_id)?.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(msg.created_at).toLocaleString()}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.primary" sx={{ mb: 1 }}>
                      {msg.content}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button 
            variant="outlined" 
            color="primary"
            startIcon={<ArrowBackIcon />}
            onClick={() => {
              if (userType === 'physician') {
                navigate('/clinic-dashboard');
              } else {
                navigate('/dashboard');
              }
            }}
          >
            Back to Dashboard
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default Messages;
